import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Bell, CheckCheck, Loader2, Truck, ClipboardCheck, DollarSign, MessageSquare, AlertTriangle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category → icon + accent colors. Phase-1 lookup; backend sets the category.
const CATEGORY_META = {
  load:     { Icon: Truck,           bg: 'bg-blue-100',    color: 'text-blue-700',    dot: 'bg-blue-500' },
  approval: { Icon: ClipboardCheck,  bg: 'bg-amber-100',   color: 'text-amber-700',   dot: 'bg-amber-500' },
  payroll:  { Icon: DollarSign,      bg: 'bg-emerald-100', color: 'text-emerald-700', dot: 'bg-emerald-500' },
  message:  { Icon: MessageSquare,   bg: 'bg-violet-100',  color: 'text-violet-700',  dot: 'bg-violet-500' },
  warning:  { Icon: AlertTriangle,   bg: 'bg-red-100',     color: 'text-red-700',     dot: 'bg-red-500' },
  info:     { Icon: Info,            bg: 'bg-slate-100',   color: 'text-slate-700',   dot: 'bg-slate-400' },
};

const POLL_INTERVAL_MS = 15000;

const NotificationsBell = () => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  const fetchCount = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setCount(r.data?.count || 0);
    } catch {
      // ignore
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications?limit=30`, { withCredentials: true });
      setItems(r.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, POLL_INTERVAL_MS);
    // Phase-1: also expose a global event so any action in the app can trigger a refresh
    const onAct = () => fetchCount();
    window.addEventListener('civilx:notifications-refresh', onAct);
    // Refresh when the tab regains focus — common case after navigating away & back
    document.addEventListener('visibilitychange', onAct);
    return () => {
      clearInterval(id);
      window.removeEventListener('civilx:notifications-refresh', onAct);
      document.removeEventListener('visibilitychange', onAct);
    };
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleOpenNotification = async (n) => {
    if (!n.read) {
      try {
        await axios.post(`${API}/notifications/${n.id}/read`, {}, { withCredentials: true });
      } catch { /* non-fatal */ }
    }
    setOpen(false);
    fetchCount();
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setItems((prev) => prev.map((p) => ({ ...p, read: true })));
      setCount(0);
    } catch { /* non-fatal */ }
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="notifications-bell">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="notifications-bell-trigger"
        className="relative w-9 h-9 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" strokeWidth={2.25} />
        {count > 0 && (
          <span
            data-testid="notifications-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-[10px] font-bold text-white flex items-center justify-center"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-md shadow-xl border border-slate-200 z-50 overflow-hidden"
          data-testid="notifications-panel"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Notifications
            </h3>
            {count > 0 && (
              <button
                type="button"
                onClick={handleMarkAll}
                data-testid="mark-all-read"
                className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <CheckCheck className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-6 flex items-center justify-center text-slate-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-sm text-slate-500 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                You're all caught up.
              </p>
            ) : (
              items.map((n) => {
                const meta = CATEGORY_META[n.category] || CATEGORY_META.info;
                const { Icon, bg, color, dot } = meta;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleOpenNotification(n)}
                    data-testid={`notification-item-${n.id}`}
                    data-category={n.category}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${n.read ? '' : 'bg-blue-50/40'}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`relative w-8 h-8 rounded-md ${bg} ${color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-4 h-4" strokeWidth={2.25} />
                        {!n.read && <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${dot} ring-2 ring-white`} />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                          {n.title}
                        </p>
                        <p className="text-xs text-slate-600 mt-0.5 line-clamp-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          {n.message}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          {new Date(n.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
